import { Check, Clock, Rocket } from "lucide-react";

export function Pricing() {
  const freeFeatures = [
    "Unlimited database connections",
    "MySQL, PostgreSQL, MongoDB, SQLite, Redis, Oracle, NeonDB",
    "Monaco-powered query editor",
    "Visual data grid with sorting & filtering",
    "SSH & SSL secure connections",
    "Query history & saved queries",
    "Basic data visualization",
    "Windows (macOS & Linux coming later)",
  ];

  const futureProFeatures = [
    "Scheduled automatic backups",
    "Advanced performance advisor",
    "Priority email support",
    "Data governance tools",
    "Advanced query analytics",
    "Early access to new features",
  ];

  return (
    <section id="pricing" className="relative py-32 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8">
          <p className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-4">
            Transparent Pricing
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Free During Early Access
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            We believe in building in public. Here's our honest roadmap — Nexatice is 
            completely free right now, and we're transparent about our future plans.
          </p>
        </div>

        {/* Current Status Badge */}
        <div className="flex justify-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm font-medium text-emerald-400">Currently in Early Access — 100% Free</span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16 items-stretch">
          {/* Free Tier - Current */}
          <div className="relative pricing-card rounded-2xl p-8 border-2 border-emerald-500/50 flex flex-col">
            {/* Available Now Badge */}
            <div className="absolute -top-4 left-8 z-10">
              <div className="px-4 py-1.5 bg-emerald-500 text-white text-sm font-semibold rounded-full flex items-center gap-2 shadow-lg shadow-emerald-500/30">
                <Check className="w-4 h-4" />
                Available Now
              </div>
            </div>

            <div className="mt-4 flex flex-col flex-grow">
              <h3 className="text-2xl font-bold text-white mb-2">Free Forever</h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-bold text-white">€0</span>
                <span className="text-zinc-500">/ forever</span>
              </div>
              <p className="text-zinc-400 mb-8">
                Full access to all core features. No credit card required.
              </p>

              <ul className="space-y-4 flex-grow">
                {freeFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#download"
                className="block w-full py-4 mt-8 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-center font-semibold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25"
              >
                Download Free
              </a>
            </div>
          </div>

          {/* Pro Tier - Future */}
          <div className="relative pricing-card rounded-2xl p-8 border border-white/10 flex flex-col">
            {/* Coming Soon Badge */}
            <div className="absolute -top-4 left-8 z-10">
              <div className="px-4 py-1.5 bg-zinc-700 text-zinc-300 text-sm font-semibold rounded-full flex items-center gap-2 shadow-lg">
                <Clock className="w-4 h-4" />
                Coming Early 2026
              </div>
            </div>

            <div className="mt-4 flex flex-col flex-grow">
              <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-bold text-zinc-400">€14.99</span>
                <span className="text-zinc-600">/ month</span>
              </div>
              <p className="text-zinc-500 text-sm mb-2">
                Cancel anytime. Advanced features for power users.
              </p>
              <p className="text-amber-500/70 text-xs mb-6">
                * Pricing subject to change before launch
              </p>

              <ul className="space-y-4 flex-grow">
                {futureProFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-zinc-500" />
                    </div>
                    <span className="text-zinc-500">{feature}</span>
                  </li>
                ))}
              </ul>

              <div
                className="flex items-center justify-center gap-2 w-full py-4 mt-8 bg-zinc-700 text-zinc-400 text-center font-semibold rounded-xl cursor-not-allowed"
              >
                <Clock className="w-4 h-4" />
                Coming Early 2026
              </div>
            </div>
          </div>
        </div>

        {/* Transparency Note */}
        <div className="glass-card rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
            <Rocket className="w-8 h-8 text-emerald-400" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-4">Our Promise to You</h3>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-6 leading-relaxed">
            We're building Nexatice in public because we believe in transparency. The free tier 
            will <strong className="text-white">always remain free</strong> with all the features 
            listed above. When we introduce Pro features, they'll be clearly marked as premium — 
            no surprise paywalls, no removing features you already have.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-zinc-400">
              <Check className="w-4 h-4 text-emerald-400" />
              No credit card required
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-zinc-400">
              <Check className="w-4 h-4 text-emerald-400" />
              No feature removal
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-zinc-400">
              <Check className="w-4 h-4 text-emerald-400" />
              No surprise paywalls
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

